import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

import { useSSE } from '@/hooks/userSSE/userSSE'
import OverlayBackground from '@/components/common/overlay/OverlayBackground'
import { getRiskState, initializeRiskState, clearRiskState } from '@/utils/riskStateManager'
import { useFinishConsultation } from '@/hooks/queries/chat/mutation'
import { useGetConsultationList, useGetMyStepsByLatestBlock } from '@/hooks/queries/chat'

type SSEMessageType = 'ping' | 'risk' | 'unabled' | string

interface SSEMessage {
  type: SSEMessageType
  message?: string
  level?: 'EMERGENCY' | 'UNABLE'
  data?: any
}

interface SSEViewerProps {
  autoConnect?: boolean
  consultationId?: number // 상담 ID 추가
  onConnectionChange?: (isConnected: boolean) => void // SSE 연결 상태 변경 콜백
  onConnectionRefReady?: (ref: { connect: () => void; disconnect: () => void }) => void // 연결 함수 ref 전달
}

export const SSEViewer = ({
  autoConnect = false,
  consultationId,
  onConnectionChange,
  onConnectionRefReady
}: SSEViewerProps = {}) => {
  const navigate = useNavigate()
  const { messages, isConnected, connect, disconnect } = useSSE({
    maxRetries: 10,
    heartbeatInterval: 20_000,
    autoConnect
  })
  const { mutateAsync: finishConsultation } = useFinishConsultation()
  const { refetch: refetchConsultationList } = useGetConsultationList()
  const { refetch: refetchGetMyStepsByLatestBlock } = useGetMyStepsByLatestBlock()

  // 연결 함수 ref를 부모에게 전달
  useEffect(() => {
    if (onConnectionRefReady) {
      onConnectionRefReady({ connect, disconnect })
    }
  }, [connect, disconnect, onConnectionRefReady])

  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' })
  const [missionToast, setMissionToast] = useState<{ show: boolean; missionData: string }>({
    show: false,
    missionData: ''
  })
  const [overlay, setOverlay] = useState<{ show: boolean; message: string }>({ show: false, message: '' })
  const autoNavigateTimerRef = useRef<NodeJS.Timeout | null>(null)

  // 미션 생성
  const handleAddMission = (missionData: string) => {
    if (!missionData.length) return

    const missionJson = JSON.parse(missionData)

    if (!missionJson) return

    navigate('/mission/create/new', {
      state: {
        missionJson: missionJson,
        consultationId: consultationId || Number(location.pathname.split('/')[2])
      }
    })
  }

  // 새로고침 시 상태 복원
  useEffect(() => {
    const savedState = getRiskState()
    if (savedState && savedState.showToast) {
      setToast({
        show: true,
        message: '🥲 도움이 필요하시면 1388 또는 전문 상담 기관에 연락해 보세요.'
      })
    }
  }, [])

  // riskStateChanged 이벤트 리스너
  useEffect(() => {
    const handleRiskStateChange = () => {
      const state = getRiskState()
      if (state && !state.showToast) {
        setToast({ show: false, message: '' })
      }
    }

    window.addEventListener('riskStateChanged', handleRiskStateChange)
    return () => window.removeEventListener('riskStateChanged', handleRiskStateChange)
  }, [])

  useEffect(() => {
    if (messages.length === 0) return

    const latestMessage = messages[messages.length - 1] as SSEMessage

    switch (latestMessage.type) {
      case 'ping':
        // ping 메시지는 무시
        break

      case 'risk': {
        // 모든 risk 레벨에서 카운트 증가
        const level = latestMessage.level === 'UNABLE' ? 'UNABLE' : 'EMERGENCY'
        const state = initializeRiskState(level, consultationId)

        // 3번째 risk가 아닐 때만 토스트 표시
        if (state.riskDetectionCount < 3) {
          if (latestMessage.level === 'EMERGENCY' || latestMessage.level === 'UNABLE') {
            setToast({
              show: true,
              message: latestMessage.message || '🥲 도움이 필요하시면 1388 또는 전문 상담 기관에 연락해 보세요.'
            })
          }
        }
        // 3번째 risk는 오버레이로만 처리 (riskThresholdReached 이벤트 핸들러에서)

        // risk 감지 횟수 표시 (개발용)
        console.log(`[SSE] Risk detection count: ${state.riskDetectionCount}/3 (Level: ${latestMessage.level})`)
        break
      }

      case 'mission':
        setMissionToast({
          show: true,
          missionData: latestMessage.data
        })
        break

      case 'unabled':
        // unabled 메시지는 무시 (필요시 나중에 추가 가능)
        break
    }
  }, [messages, navigate])

  // 3번째 risk 감지 시 자동 종료 처리
  useEffect(() => {
    const handleRiskThreshold = async (event: Event) => {
      const customEvent = event as CustomEvent<{ consultationId?: number; count: number }>
      const { consultationId: eventConsultationId, count } = customEvent.detail

      console.log(`[SSE] Risk threshold reached! Count: ${count}, ConsultationId: ${eventConsultationId}`)

      // 토스트 숨기기
      setToast({ show: false, message: '' })

      // 오버레이 표시
      setOverlay({
        show: true,
        message: '안전을 위해 상담을 종료합니다.'
      })

      // 상담 종료 API 호출
      if (eventConsultationId) {
        try {
          await finishConsultation({
            consultation_id: eventConsultationId,
            body: {
              skip: true,
              reason: '위험 감지로 인한 자동 종료'
            }
          })
          refetchConsultationList()
          refetchGetMyStepsByLatestBlock()

          console.log('[SSE] Consultation finished due to risk detection')
        } catch (error) {
          console.error('[SSE] Failed to finish consultation:', error)
        }
      }

      // 5초 후 자동으로 홈으로 이동
      autoNavigateTimerRef.current = setTimeout(() => {
        clearRiskState()
        setOverlay({ show: false, message: '' })
        navigate('/')
      }, 5000)
    }

    window.addEventListener('riskThresholdReached', handleRiskThreshold)

    return () => {
      window.removeEventListener('riskThresholdReached', handleRiskThreshold)
      // 컴포넌트 언마운트 시 타이머 정리
      if (autoNavigateTimerRef.current) {
        clearTimeout(autoNavigateTimerRef.current)
      }
    }
  }, [navigate, finishConsultation])

  // 연결 상태 변경 시 부모 컴포넌트에게 알림
  useEffect(() => {
    onConnectionChange?.(isConnected)
  }, [isConnected, onConnectionChange])

  // 컴포넌트가 마운트될 때 수동으로 연결
  useEffect(() => {
    let mounted = true

    if (!autoConnect && mounted) {
      connect()
    }

    // 컴포넌트가 언마운트될 때 연결 종료
    return () => {
      mounted = false
      if (!autoConnect) {
        disconnect()
      }
    }
  }, [])

  return (
    <>
      {/* 상단 토스트 메시지 - risk 알림용 */}
      {toast.show && (
        <div className='fixed top-24 left-4 right-4 z-50 animate-in slide-in-from-top duration-300'>
          <div className='bg-orange-500 text-white px-4 py-4 rounded-2xl shadow-lg flex items-center gap-3'>
            <span className='text-sm font-medium flex-1'>{toast.message}</span>
          </div>
        </div>
      )}

      {/* 상단 토스트 메시지 - 미션 생성 알림용 */}
      {missionToast.show && (
        <div className='fixed top-24 left-4 right-4 z-50 animate-in slide-in-from-top duration-300'>
          <div className='bg-orange-500/80 text-white px-4 py-4 rounded-2xl shadow-lg flex items-center gap-3'>
            <button onClick={() => setMissionToast({ ...missionToast, show: false })} className=' mr-2'>
              <svg
                width='22'
                height='22'
                viewBox='0 0 22 22'
                fill='none'
                xmlns='http://www.w3.org/2000/svg'
                style={{ flex: 'none' }}
              >
                <circle cx='11' cy='11' r='10' stroke='white' strokeWidth='2' fill='none' />
                <line x1='7' y1='7' x2='15' y2='15' stroke='white' strokeWidth='2' strokeLinecap='round' />
                <line x1='15' y1='7' x2='7' y2='15' stroke='white' strokeWidth='2' strokeLinecap='round' />
              </svg>
            </button>
            <span className=' flex-1 font-medium'>미션이 도착했어요!</span>
            <button onClick={() => handleAddMission(missionToast.missionData)} className='text-sm font-light'>
              추가하기
            </button>
          </div>
        </div>
      )}

      {/* 오버레이 */}
      {overlay.show && (
        <OverlayBackground>
          <div className='text-white flex flex-col items-center'>
            <div className='mb-4 text-4xl'>⚠️</div>
            <div className='mb-4 text-xl font-bold'>알림</div>
            <div className='mb-6 text-center'>{overlay.message}</div>
            <button
              onClick={() => {
                // 타이머 정리
                if (autoNavigateTimerRef.current) {
                  clearTimeout(autoNavigateTimerRef.current)
                }
                clearRiskState()
                setOverlay({ show: false, message: '' })
                navigate('/')
              }}
              className='px-6 py-3 bg-white text-gray-800 rounded-full font-semibold hover:bg-gray-100 transition-colors'
            >
              홈으로 이동
            </button>
            <div className='mt-4 text-sm opacity-60'>5초 후 자동으로 이동합니다</div>
          </div>
        </OverlayBackground>
      )}

      {/* 개발 모드에서만 연결 상태 표시 */}
      {import.meta.env.DEV && (
        <div className='fixed top-4 right-4 text-xs bg-gray-800 text-white px-2 py-1 rounded'>
          SSE: {isConnected ? '✅' : '❌'}
        </div>
      )}
    </>
  )
}
